import { ref, watch, computed, type Ref } from 'vue';
import type { FormField } from '../types/formTypeStructure';

/**
 * Hook pour gérer le filtrage dynamique des champs de formulaire
 */
export function useDynamicFiltering(
  fields: FormField[],
  formValues: Ref<Record<string, any>>,
  setValue: (fieldName: string, value: any, options?: { shouldValidate?: boolean }) => void
) {
  const filteredFields = ref<FormField[]>(fields);
  const previousValuesRef = ref<Record<string, any>>({});

  // Fonction pour appliquer le filtrage à un champ
  const applyDynamicFilter = (field: FormField, formValues: Record<string, any>): FormField => {
    if (!field.dynamicFilterRule || field.fieldType !== 'dropdown') {
      return field;
    }

    const { dependentFieldName, dataSource } = field.dynamicFilterRule;
    const dependentValue = formValues[dependentFieldName];

    console.log('🔍 [DynamicFiltering] Application du filtre:', {
      fieldName: field.fieldName,
      dependentField: dependentFieldName,
      dependentValue,
      hasDataSource: !!dataSource.data
    });

    // Si le champ parent est vide ou n'a pas de données correspondantes, vider les options
    if (!dependentValue || !dataSource.data[dependentValue]) {
      console.log('⚠️ [DynamicFiltering] Aucune donnée trouvée pour:', dependentValue, '- Options vidées');
      return {
        ...field,
        selectOptions: [] // Vider les options au lieu de retourner le champ tel quel
      };
    }

    const filteredOptions = dataSource.data[dependentValue];
    console.log('✅ [DynamicFiltering] Options filtrées:', {
      fieldName: field.fieldName,
      optionsCount: filteredOptions.length,
      options: filteredOptions.map(opt => opt.value)
    });

    return {
      ...field,
      selectOptions: filteredOptions
    };
  };

  // Fonction pour trouver les champs qui dépendent d'un champ donné
  const findDependentFields = (fieldName: string): FormField[] => {
    return fields.filter(field => 
      field.dynamicFilterRule?.dependentFieldName === fieldName
    );
  };

  // Fonction récursive pour réinitialiser une cascade de champs dépendants
  const resetCascade = (
    fieldName: string,
    formValues: Record<string, any>
  ) => {
    const dependentFields = findDependentFields(fieldName);
    
    dependentFields.forEach(dependentField => {
      const currentValue = formValues[dependentField.fieldName];
      if (currentValue) {
        console.log(`🔄 [DynamicFiltering] Réinitialisation cascade: ${dependentField.fieldName} (dépend de ${fieldName})`);
        // Réinitialiser la valeur mais PAS les options (elles seront mises à jour par le filtrage)
        setValue(dependentField.fieldName, "", { shouldValidate: false });
      }
      
      // Réinitialiser récursivement les champs qui dépendent de celui-ci
      resetCascade(dependentField.fieldName, formValues);
    });
  };

  // Fonction pour appliquer le filtrage à tous les champs
  const applyFiltersToFields = (fields: FormField[], formValues: Record<string, any>): FormField[] => {
    return fields.map(field => applyDynamicFilter(field, formValues));
  };

  // Surveiller les changements dans le formulaire
  watch(
    formValues,
    (newValues, oldValues) => {
      console.log('👁️ [DynamicFiltering] Changement détecté dans le formulaire', { newValues, oldValues });
      
      // TOUJOURS appliquer le filtrage avec les nouvelles valeurs
      const updatedFields = applyFiltersToFields(fields, newValues);
      console.log('📊 [DynamicFiltering] Champs après filtrage:', updatedFields.map(f => ({
        fieldName: f.fieldName,
        hasOptions: !!f.selectOptions?.length,
        optionsCount: f.selectOptions?.length || 0
      })));
      filteredFields.value = updatedFields;
      
      // Ensuite, si un champ parent change ou devient vide, réinitialiser les cascades
      if (oldValues) {
        // Trouver les champs qui ont changé
        Object.keys(newValues).forEach(changedFieldName => {
          const previousValue = previousValuesRef.value[changedFieldName];
          const currentValue = newValues[changedFieldName];
          
          if (previousValue !== currentValue) {
            const changedField = fields.find(f => f.fieldName === changedFieldName);
            
            // Si c'est un champ parent (qui n'a pas de dynamicFilterRule) et que sa valeur a changé
            if (changedField && !changedField.dynamicFilterRule) {
              // Si le champ est devenu vide OU si on change d'une valeur à une autre (pas la première sélection)
              if (!currentValue || (previousValue !== undefined && previousValue !== null && previousValue !== "")) {
                console.log(`🔄 [DynamicFiltering] Champ parent ${changedFieldName} a changé, réinitialisation cascade`, {
                  previousValue,
                  currentValue
                });
                // Utiliser setTimeout pour laisser le temps au filtrage de s'appliquer
                setTimeout(() => {
                  resetCascade(changedFieldName, newValues);
                }, 0);
              } else {
                console.log(`✅ [DynamicFiltering] Première sélection du champ parent ${changedFieldName}, pas de réinitialisation`);
              }
            }
          }
        });
      }
      
      // Mettre à jour la référence des valeurs précédentes
      previousValuesRef.value = { ...newValues };
    },
    { deep: true, immediate: true }
  );

  return computed(() => filteredFields.value);
}

/**
 * Hook pour obtenir les champs avec filtrage appliqué pour une section spécifique
 */
export function useSectionDynamicFiltering(
  sectionFields: FormField[],
  formValues: Ref<Record<string, any>>,
  setValue: (fieldName: string, value: any, options?: { shouldValidate?: boolean }) => void
) {
  const filteredSectionFields = ref<FormField[]>(sectionFields);
  const previousValuesRef = ref<Record<string, any>>({});

  // Fonction pour trouver les champs qui dépendent d'un champ donné (dans cette section)
  const findDependentFields = (fieldName: string): FormField[] => {
    return sectionFields.filter(field => 
      field.dynamicFilterRule?.dependentFieldName === fieldName
    );
  };

  // Fonction récursive pour réinitialiser une cascade de champs dépendants
  const resetCascade = (
    fieldName: string,
    formValues: Record<string, any>
  ) => {
    const dependentFields = findDependentFields(fieldName);
    
    dependentFields.forEach(dependentField => {
      const currentValue = formValues[dependentField.fieldName];
      if (currentValue) {
        console.log(`🔄 [SectionDynamicFiltering] Réinitialisation cascade: ${dependentField.fieldName} (dépend de ${fieldName})`);
        setValue(dependentField.fieldName, "", { shouldValidate: false });
      }
      
      // Réinitialiser récursivement les champs qui dépendent de celui-ci
      resetCascade(dependentField.fieldName, formValues);
    });
  };

  // Fonction pour appliquer le filtrage à un champ
  const applyDynamicFilter = (field: FormField, formValues: Record<string, any>): FormField => {
    if (!field.dynamicFilterRule || field.fieldType !== 'dropdown') {
      return field;
    }

    const { dependentFieldName, dataSource } = field.dynamicFilterRule;
    const dependentValue = formValues[dependentFieldName];

    // Si le champ parent est vide ou n'a pas de données correspondantes, vider les options
    if (!dependentValue || !dataSource.data[dependentValue]) {
      return {
        ...field,
        selectOptions: []
      };
    }

    const filteredOptions = dataSource.data[dependentValue];

    return {
      ...field,
      selectOptions: filteredOptions
    };
  };

  // Fonction pour appliquer le filtrage à tous les champs de la section
  const applyFiltersToFields = (fields: FormField[], formValues: Record<string, any>): FormField[] => {
    return fields.map(field => applyDynamicFilter(field, formValues));
  };

  // Surveiller les changements dans le formulaire (seulement pour les champs de cette section)
  watch(
    formValues,
    (newValues, oldValues) => {
      // Appliquer le filtrage avec les nouvelles valeurs
      const updatedFields = applyFiltersToFields(sectionFields, newValues);
      filteredSectionFields.value = updatedFields;
      
      // Réinitialiser les cascades si nécessaire
      if (oldValues) {
        Object.keys(newValues).forEach(changedFieldName => {
          const previousValue = previousValuesRef.value[changedFieldName];
          const currentValue = newValues[changedFieldName];
          
          if (previousValue !== currentValue) {
            const changedField = sectionFields.find(f => f.fieldName === changedFieldName);
            
            if (changedField && !changedField.dynamicFilterRule) {
              if (!currentValue || (previousValue !== undefined && previousValue !== null && previousValue !== "")) {
                setTimeout(() => {
                  resetCascade(changedFieldName, newValues);
                }, 0);
              }
            }
          }
        });
      }
      
      previousValuesRef.value = { ...newValues };
    },
    { deep: true, immediate: true }
  );

  return computed(() => filteredSectionFields.value);
}

